/** Code-handoff SSO — the browser side (Phase 0a, the diwata.id-ready path).
 *
 *  one_login_spec.md §4, mirroring Diwa/Conclave: we send the browser to the
 *  gate with Apex's client id and our REGISTERED redirect URI; the gate returns
 *  a one-time code + our state on that callback; the code goes to the Daemon
 *  (Apex's backend) which redeems it server-side and mints the Daemon's OWN
 *  ``apex_session`` cookie. These are the pure pieces (URL building, state
 *  round-trip) so they test without a DOM. The gate origin + client id always
 *  ARRIVE from the backend's /auth/gate/status — never hardcode a gate host.
 *
 *  This is the successor to the plain-`next` bounce (main.tsx / SignOutButton):
 *  that path assumed the shared .diwata.domains cookie (works only same-
 *  registrable-domain); this one does not, so it survives the gate's move to
 *  diwata.id.
 */

const STATE_KEY = 'ap-gate-sso'

/** The callback URI Apex registers at the gate: <origin>/auth/callback.
 *  Always the SPA's own origin, independent of where the Daemon lives. */
export function callbackRedirectUri(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/auth/callback`
}

/** Unguessable per-attempt state (CSRF binding for the round-trip). */
export function makeState(): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function buildGateLoginUrl(
  gateOrigin: string,
  clientId: string,
  redirectUri: string,
  state: string,
): string {
  const url = new URL('/login', gateOrigin)
  url.searchParams.set('client_id', clientId)
  url.searchParams.set('redirect_uri', redirectUri)
  url.searchParams.set('state', state)
  return url.toString()
}

/** Park the state (and where the visitor was headed) for the callback leg. */
export function rememberState(storage: Storage, state: string, from: string | null = null): void {
  storage.setItem(STATE_KEY, JSON.stringify({ state, from }))
}

/** Validate + consume the parked state. Single-use: reading clears it, match or
 *  not, so a replayed callback can never pass twice. */
export function consumeState(
  storage: Storage,
  returned: string | null,
): { ok: boolean; from: string | null } {
  const raw = storage.getItem(STATE_KEY)
  storage.removeItem(STATE_KEY)
  if (!raw || !returned) return { ok: false, from: null }
  try {
    const parsed = JSON.parse(raw) as { state?: string; from?: string | null }
    return { ok: !!parsed.state && parsed.state === returned, from: parsed.from ?? null }
  } catch {
    return { ok: false, from: null }
  }
}
